import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {GlobalFilters, DateRange, EvidenceInclusion} from "./types";

// Default values for store state
const defaultGlobalFilters: GlobalFilters = {
  owner: undefined,
  productArea: undefined,
  dateRange: null
};

const defaultEvidenceInclusion: EvidenceInclusion = 'total';

interface IdeasStore {
  globalFilters: GlobalFilters;
  evidenceInclusion: EvidenceInclusion;
  setEvidenceInclusion: (value: EvidenceInclusion) => void;
  setGlobalFilters: (value: GlobalFilters) => void;
  setOwner: (value: string[] | null) => void;
  setProductArea: (value: string[] | null) => void;
  setDateRange: (value: DateRange | null) => void;
  // Helper
  getCurrentGlobalFilters: () => GlobalFilters;
  getCurrentEvidenceInclusion: () => EvidenceInclusion;
}

export const useIdeasStore = create<IdeasStore>()(
  persist(
    (set, get) => ({
      // Initial State
      globalFilters: defaultGlobalFilters,
      evidenceInclusion: defaultEvidenceInclusion,

      setGlobalFilters: (value: GlobalFilters) => {
        set({ globalFilters: value });
      },

      setEvidenceInclusion: (value: EvidenceInclusion) => {
        set({ evidenceInclusion: value });
      },

      setOwner: (value: string[] | null) => {
        const newValue = value || undefined;
        set((state) => {
          if (state.globalFilters.owner !== newValue) {
            return {
              globalFilters: {
                ...state.globalFilters,
                owner: newValue
              }
            };
          }
          return state;
        });
      },

      setProductArea: (value: string[] | null) => {
        const newValue = value || undefined;
        set((state) => {
          if (state.globalFilters.productArea !== newValue) {
            return {
              globalFilters: {
                ...state.globalFilters,
                productArea: newValue
              }
            };
          }
          return state;
        });
      },

      setDateRange: (value: DateRange | null) => {
        console.log('🔍 [ideasStore] setDateRange called with:', value);
        set((state) => {
          const current = state.globalFilters.dateRange;
          // Deep comparison for DateRange objects
          if (value === null && (current === null || current === undefined)) {
            return state;
          }
          if (value === null || current === null || current === undefined) {
            return {
              globalFilters: {
                ...state.globalFilters,
                dateRange: value
              }
            };
          }
          // Both are non-null DateRange objects, compare values
          if (current.start !== value.start || current.end !== value.end) {
            return {
              globalFilters: {
                ...state.globalFilters,
                dateRange: value
              }
            };
          }
          return state;
        });
      },
      
      // Helper
      getCurrentGlobalFilters: () => get().globalFilters,

      getCurrentEvidenceInclusion: () => get().evidenceInclusion,
    }),
    {
      name: 'ideas-store',
      partialize: (state) => ({
        globalFilters: state.globalFilters,
        evidenceInclusion: state.evidenceInclusion
      }),
      // Merge persisted state with defaults to handle corrupted/incomplete storage
      merge: (persistedState, currentState) => {
        const persisted = persistedState as Partial<IdeasStore> | undefined;
        return {
          ...currentState,
          globalFilters: persisted?.globalFilters ?? defaultGlobalFilters,
          evidenceInclusion: persisted?.evidenceInclusion ?? defaultEvidenceInclusion,
        };
      },
    }
  )
);

